import {
	GlobalNavigationItemKind,
	GlobalNavigationPermissionsModel
} from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { SpaceGlobalNavigationListItem } from "./SpaceGlobalNavigationListItem";
import { AppGlobalNavigationItemInfo } from "./GlobalNavigationManagement";
import { AppGlobalNavigationListItem } from "./AppGlobalNavigationListItem";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { GroupGlobalNavigationListItem } from "./GroupGlobalNavigationListItem";
import { DeleteConfirmationDialogData } from "./DeleteConfirmationDialog";
import { GlobalNavigationGroupAndIndex } from "./GlobalNavigationItemMenu";
import { getClientId } from "client/Misc/clientId";
import { never } from "client_legacy/ndaw/errors";
import {
	AppGlobalNavigationItemFormModel,
	GlobalNavigationItemFormModel,
	GroupGlobalNavigationItemFormModel,
	isAppItem,
	isSpaceItem,
	SpaceGlobalNavigationItemFormModel
} from "../misc";
import { AutoFocusState } from "client/Hooks/useAutoFocus";
import { Guid } from "client_legacy/ndaw/guid";
import { SavedSpace } from "client/Features/Workspace/EditSpaceDialog/EditSpaceDialog";

export type GlobalNavigationListItemProps = {
	index: number;
	itemHandler: IValueInputAttachable<GlobalNavigationItemFormModel>;
	autoFocusState: AutoFocusState<Guid>;
	doesMatchSearchTerm(item: GlobalNavigationItemFormModel): boolean;
	onDelete(index: number): void;
	onRemoveApp(installedAppId: number): void;
	onUnGroupChildren?(groupIndex: number): void;
	permissions: GlobalNavigationPermissionsModel;
	onCreateSharedSpace: () => Promise<TryResult<SavedSpace>>;
	onEditSpace: (id: number) => void;
	appItemsInfo: Map<number, AppGlobalNavigationItemInfo>;
	onOpenDeleteConfirmation(data: DeleteConfirmationDialogData): Promise<boolean>;
	onAddAppAsTab?(targetGroupIndex: number | null): void;
	groups: readonly GlobalNavigationGroupAndIndex[];
	containingGroupIndex: number | null;
	onMoveToGroup(
		itemIndex: number,
		sourceGroupIndex: number | null,
		targetGroupIndex: number
	): void;
	onMoveOutOfGroup(
		itemIndex: number,
		sourceGroupIndex: number
	): void;
	appIdToScroll: Guid | null;
	onAppIdToScrollChange: (id: Guid | null) => void;
	onItemAdded: () => void;
	onChangeIsBusy: (isBusy: boolean) => void;
};

export const GlobalNavigationListItem = (props: GlobalNavigationListItemProps): ReactElement => {
	const {
		index,
		itemHandler,
		autoFocusState,
		doesMatchSearchTerm,
		onDelete,
		permissions,
		onCreateSharedSpace,
		onEditSpace,
		appItemsInfo,
		onRemoveApp,
		onOpenDeleteConfirmation,
		onUnGroupChildren,
		onAddAppAsTab,
		groups,
		containingGroupIndex,
		onMoveOutOfGroup,
		onMoveToGroup,
		appIdToScroll,
		onAppIdToScrollChange,
		onItemAdded,
		onChangeIsBusy
	} = props;

	const deleteHandler = useCallback(
		(): void => {
			return onDelete(index);
		},
		[index, onDelete]
	);

	const unGroupChildrenHandler = useMemo(
		(): (() => void) | undefined => {
			if (!onUnGroupChildren) {
				return undefined;
			}

			return () => onUnGroupChildren(index);
		},
		[index, onUnGroupChildren]
	);

	const addAppAsTabHandler = useMemo(
		(): (() => void) | undefined => {
			if (!onAddAppAsTab) {
				return undefined;
			}

			return () => onAddAppAsTab(index);
		},
		[index, onAddAppAsTab]
	);

	const renderSpaceItem = (
		spaceItemHandler: IValueInputAttachable<SpaceGlobalNavigationItemFormModel>
	): ReactElement => {
		return (
			<SpaceGlobalNavigationListItem
				itemHandler={spaceItemHandler}
				autoFocusState={autoFocusState}
				onDelete={deleteHandler}
				onEdit={onEditSpace}
				onOpenDeleteConfirmation={onOpenDeleteConfirmation}
				groups={groups}
				containingGroupIndex={containingGroupIndex}
				onMoveOutOfGroup={onMoveOutOfGroup}
				onMoveToGroup={onMoveToGroup}
				index={index}
				onChangeIsBusy={onChangeIsBusy}
			/>
		);
	};

	const renderAppItem = (
		appItemHandler: IValueInputAttachable<AppGlobalNavigationItemFormModel>
	): ReactElement => {
		const {
			installedAppId
		} = appItemHandler.value
			.appData;

		const itemInfo: AppGlobalNavigationItemInfo | undefined = appItemsInfo
			.get(installedAppId);

		if (!itemInfo) {
			throw Error(`There is no information found for the app item with id ${installedAppId}`);
		}

		return (
			<AppGlobalNavigationListItem
				clientId={getClientId(itemHandler.value)}
				name={itemInfo.name}
				image={itemInfo.image}
				color={itemInfo.color}
				permissions={permissions}
				onDelete={deleteHandler}
				onRemoveApp={onRemoveApp}
				onOpenDeleteConfirmation={onOpenDeleteConfirmation}
				installedAppId={installedAppId}
				groups={groups}
				containingGroupIndex={containingGroupIndex}
				onMoveOutOfGroup={onMoveOutOfGroup}
				onMoveToGroup={onMoveToGroup}
				index={index}
				appIdToScroll={appIdToScroll}
				onAppIdToScrollChange={onAppIdToScrollChange}
			/>
		);
	};

	const renderGroupItem = (
		groupItemHandler: IValueInputAttachable<GroupGlobalNavigationItemFormModel>,
		index: number
	): ReactElement => {
		const group: GroupGlobalNavigationItemFormModel = groupItemHandler.value;

		const trackingId: string = isNotNil(group.id)
			? generateTrackingId("group", `|${group.id}`)
			: generateTrackingId("new-group", `|${index}`);

		return (
			<GroupGlobalNavigationListItem
				groupHandler={groupItemHandler}
				autoFocusState={autoFocusState}
				appItemsInfo={appItemsInfo}
				doesMatchSearchTerm={doesMatchSearchTerm}
				onDelete={deleteHandler}
				onCreateSharedSpace={onCreateSharedSpace}
				onEditSpace={onEditSpace}
				permissions={permissions}
				trackingId={trackingId}
				onOpenDeleteConfirmation={onOpenDeleteConfirmation}
				onRemoveApp={onRemoveApp}
				onUnGroupChildren={unGroupChildrenHandler}
				onAddAppAsTab={addAppAsTabHandler}
				groups={groups}
				index={index}
				onMoveOutOfGroup={onMoveOutOfGroup}
				onMoveToGroup={onMoveToGroup}
				appIdToScroll={appIdToScroll}
				onAppIdToScrollChange={onAppIdToScrollChange}
				onItemAdded={onItemAdded}
				onChangeIsBusy={onChangeIsBusy}
			/>
		);
	};

	return itemHandler.whenType(
		isSpaceItem,
		spaceItemHandler => renderSpaceItem(spaceItemHandler),
		otherItemHandler => otherItemHandler.whenType(
			isAppItem,
			appItemHandler => renderAppItem(appItemHandler),
			groupItemHandler => renderGroupItem(groupItemHandler, index)
		)
	);
};
GlobalNavigationListItem.displayName = "GlobalNavigationListItem";

export function getItemUniqueKey(item: GlobalNavigationItemFormModel): string {
	if (isNotNil(item.id)) {
		return `item-${item.id}`;
	}

	switch (item.kind) {
		case GlobalNavigationItemKind.Space:
			return `space-db${item.spaceData.spaceId}`;
		case GlobalNavigationItemKind.InstalledApp:
			return `app-${item.appData.installedAppId}`;
		case GlobalNavigationItemKind.Group:
			return `group-${getClientId(item)}`;
		default:
			never(item);
	}
}
